import { HeatingType, SearchFormNlpResult } from '../../types/search';

export interface Filters {
  minBeds?: string;
  maxBeds?: string;
  minPrice?: string;
  maxPrice?: string;
  radius?: string;
  expand?: string;
  currency?: string;
  category?: string;
  sortOrder?: string;
  includeGone?: boolean;
  includeAgreed?: boolean;
  excludePoa?: boolean;
  excludeBuildingSites?: boolean;
  excludeStudentLettings?: boolean;
  unfurnished?: boolean;
  furnished?: boolean;
  partiallyFurnished?: boolean;
  optionalFurnished?: boolean;
  polyIds?: number[];
  saleType?: string;
  styles?: string[];
  term?: string;
  filter?: Array<{ filterValue: string; description: string }>;
  lat?: number | null;
  lng?: number | null;
  heatingType?: HeatingType[];
  epc?: string;
  includePotentialEpc?: boolean;
  yearBuilt?: string;
  minInternalArea?: string;
  broadband?: string;
  garage?: boolean;
  largeGarage?: boolean;
  garageSize?: string | null;
  tags?: string[] | null;
  excludeTags?: string[] | null;
  coOwnership?: boolean;
}

interface Result {
  nlpFilters: Filters;
  excludeFilters: Filters;
}

/**
 * Converts params object from API into an object that can be saved in search reducer
 */

const getNlPFilters = (
  params?: SearchFormNlpResult['params'],
  filterItems?: SearchFormNlpResult['filterItems'],
  lastParams?: SearchFormNlpResult['params'],
): Result => {
  if (!params) {
    return {
      nlpFilters: {},
      excludeFilters: {},
    };
  }

  const filters: Filters = {};
  const excludeFilters: Filters = {};

  // If a user has overwritten a filter via nlp, removing it via nlp should remove it from app filters
  if (lastParams) {
    if (lastParams.minBeds && !params.minBeds) excludeFilters.minBeds = '';
    if (lastParams.maxBeds && !params.maxBeds) excludeFilters.maxBeds = '';
    if (lastParams.minPrice && !params.minPrice) excludeFilters.minPrice = '';
    if (lastParams.maxPrice && !params.maxPrice) excludeFilters.maxPrice = '';
    if (lastParams.currency && !params.currency) excludeFilters.currency = 'GBP';
    if (lastParams.radius && !params.radius) excludeFilters.radius = '';
    if (lastParams.expand && !params.expand) excludeFilters.expand = '';
    if (lastParams.sort && !params.sort) excludeFilters.sortOrder = '';
    if (lastParams.gone && !params.gone) excludeFilters.includeGone = false;
    if (lastParams.agreed && !params.agreed) excludeFilters.includeAgreed = false;
    if (lastParams.excludePoa && !params.excludePoa) excludeFilters.excludePoa = false;
    if (lastParams.styleGroup && !params.styleGroup) excludeFilters.styles = [];
    if (lastParams.heatingType && !params.heatingType) excludeFilters.heatingType = [];
    if (lastParams.epc && !params.epc) excludeFilters.epc = '';
    if (lastParams.epcRatingType && !params.epcRatingType) excludeFilters.includePotentialEpc = false;
    if (lastParams.minYearBuilt && !params.minYearBuilt) excludeFilters.yearBuilt = '';
    if (lastParams.maxYearBuilt && !params.maxYearBuilt) excludeFilters.yearBuilt = '';
    if (lastParams.minInternalArea && !params.minInternalArea) excludeFilters.minInternalArea = '';
    if (lastParams.broadband && !params.broadband) excludeFilters.broadband = '';
    if (lastParams.garage && !params.garage) excludeFilters.garage = false;
    if (lastParams.largeGarage && !params.largeGarage) excludeFilters.largeGarage = false;
    if (lastParams.garageSize && !params.garageSize) excludeFilters.garageSize = null;
    if (lastParams.tags && !params.tags) excludeFilters.tags = null;
    if (lastParams.excludeTags && !params.excludeTags) excludeFilters.excludeTags = null;
    if (lastParams.coown && !params.coown) excludeFilters.coOwnership = false;
  }

  if (params.minBeds) filters.minBeds = String(params.minBeds);
  if (params.maxBeds) filters.maxBeds = String(params.maxBeds);
  if (params.minPrice) filters.minPrice = String(params.minPrice);
  if (params.maxPrice) filters.maxPrice = String(params.maxPrice);
  if (params.currency) filters.currency = String(params.currency);
  if (params.radius) filters.radius = String(params.radius);
  if (params.expand) filters.expand = String(params.expand);
  if (params.sort) filters.sortOrder = params.sort;
  if (params.gone) filters.includeGone = true;
  if (params.agreed) filters.includeAgreed = true;
  if (params.excludePoa) filters.excludePoa = true;
  if (params.heatingType?.length) filters.heatingType = [...params.heatingType];
  if (params.epc) filters.epc = params.epc;
  if (params.epcRatingType === 'EITHER') filters.includePotentialEpc = true;
  if (params.minInternalArea) filters.minInternalArea = String(params.minInternalArea);
  if (params.broadband?.length) filters.broadband = params.broadband[0];
  if (params.garage) {
    filters.garage = true;
    filters.largeGarage = false;
  }
  if (params.largeGarage) filters.largeGarage = true;
  if (params.garageSize) filters.garageSize = String(params.garageSize);
  if (params.tags) filters.tags = [...params.tags];
  if (params.excludeTags) filters.excludeTags = [...params.excludeTags];
  if (typeof params.coown === 'boolean') filters.coOwnership = params.coown;

  if (params.minYearBuilt || params.maxYearBuilt) {
    filters.yearBuilt = `${params.minYearBuilt || 0}-${params.maxYearBuilt || 0}`;
  }

  if (
    params.category &&
    params.category[0] === 'residential' &&
    ((params.f && params.f.includes('att:20')) || (params.filter && params.filter.includes('att:20')))
  ) {
    filters.category = 'student';
    filters.saleType = 'rent';
  } else if (params.category) {
    filters.category = params.category[0];
    filters.saleType = params.saleType && params.saleType[0] ? params.saleType[0] : 'sale';
  }

  if (params.styleGroup) {
    filters.styles = [...params.styleGroup];
  }

  if (params.ft) {
    filters.furnished = params.ft.includes('fullyFurnished');
    filters.unfurnished = params.ft.includes('unfurnished');
    filters.partiallyFurnished = params.ft.includes('partlyFurnished');
    filters.optionalFurnished = params.ft.includes('optional');
  }

  if (filterItems) {
    filters.filter = filterItems.filter((item) => {
      return item.description !== 'Student';
    });
  }

  if (params.polygonId) {
    filters.polyIds = [...params.polygonId];
  }

  if (params.rlat && params.rlng) {
    filters.lat = params.rlat;
    filters.lng = params.rlng;
  }

  return {
    nlpFilters: filters,
    excludeFilters,
  };
};

export default getNlPFilters;
